import { ReactRenderer } from '@tiptap/react';
import Mention from '@tiptap/extension-mention';
import type { SuggestionOptions, SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import MentionList from '../components/MentionList';
import { registerDismissibleLayer } from '../hooks/useDismiss';
import type { ChannelMember } from '../lib/api';

export interface MentionSuggestionItem {
  id: string;
  label: string;
  role: string;
}

/** What MentionList needs on top of what @tiptap/suggestion hands the renderer. */
export interface MentionListExtraProps {
  channelType?: 'dm' | 'channel';
}

const MentionWithMarkdown = Mention.extend({
  addStorage() {
    return {
      ...(this.parent?.() ?? {}),
      markdown: {
        serialize(state: { write(s: string): void }, node: { attrs: { id: string } }) {
          state.write(`<@${node.attrs.id}>`);
        },
        parse: {},
      },
    };
  },
});

export function createMentionExtension(
  getUsersFn: () => ChannelMember[],
  activeRef?: { current: boolean },
  // In a DM the candidate list is capped at two by the server, so a query that
  // matches neither party would otherwise collapse to nothing. MentionList
  // renders a placeholder saying a DM is always two people instead, and it
  // needs the channel type to know when that applies.
  getChannelTypeFn?: () => 'dm' | 'channel' | undefined,
) {
  return MentionWithMarkdown.configure({
    HTMLAttributes: {
      class: 'mention',
    },
    renderText({ node }) {
      return `<@${node.attrs.id as string}>`;
    },
    renderHTML({ node }) {
      return ['span', { class: 'mention', 'data-id': node.attrs.id }, `@${(node.attrs.label as string) ?? (node.attrs.id as string)}`];
    },
    suggestion: {
      char: '@',
      allowedPrefixes: [' ', '\n', null] as unknown as string[],
      items: ({ query }: { query: string }) => {
        const users = getUsersFn();
        const q = query.toLowerCase();
        return users
          .filter(u => u.display_name.toLowerCase().startsWith(q))
          .slice(0, 10)
          .map(u => ({ id: u.user_id, label: u.display_name, role: u.role }));
      },
      render: () => {
        let component: ReactRenderer<{ onKeyDown: (props: SuggestionKeyDownProps) => boolean }> | null = null;
        let popup: HTMLDivElement | null = null;
        let unregisterLayer: (() => void) | null = null;

        // MentionList knows to render the DM placeholder when the candidate
        // set is empty in a DM.
        const augment = (
          props: SuggestionProps<MentionSuggestionItem>,
        ): SuggestionProps<MentionSuggestionItem> & MentionListExtraProps => ({
          ...props,
          channelType: getChannelTypeFn?.(),
        });

        /**
         * Takes the popup down and off the dismissible-layer stack. The popup
         * hangs beside the composer on the normal path and off `document.body`
         * only when it cannot find one, so registering it here rather than in a
         * component covers both placements: whichever it lands in, a press on
         * it belongs to it, and Escape reaches it and nothing beneath it.
         */
        const dismiss = () => {
          unregisterLayer?.();
          unregisterLayer = null;
          popup?.remove();
          component?.destroy();
          popup = null;
          component = null;
        };

        return {
          onStart: (props: SuggestionProps<MentionSuggestionItem>) => {
            if (activeRef) activeRef.current = true;
            const element = document.createElement('div');
            element.className = 'mention-suggestion-popup';
            popup = element;
            const container = props.decorationNode?.closest('[data-message-input-root]');
            (container ?? document.body).appendChild(element);
            // Escape is answered from the registry rather than from the
            // suggestion plugin: the registry claims the key at the document in
            // the capture phase, and ProseMirror drops a keydown whose default
            // is already prevented, so a plugin's key handler never runs for it.
            unregisterLayer = registerDismissibleLayer(() => ({
              within: element,
              onEscape: dismiss,
            }));

            component = new ReactRenderer(MentionList, {
              props: augment(props),
              editor: props.editor,
            });
            element.appendChild(component.element);
          },
          onUpdate: (props: SuggestionProps<MentionSuggestionItem>) => {
            component?.updateProps(augment(props));
          },
          onKeyDown: (props: SuggestionKeyDownProps) => component?.ref?.onKeyDown(props) ?? false,
          onExit: () => {
            if (activeRef) activeRef.current = false;
            dismiss();
          },
        };
      },
    } as Partial<SuggestionOptions<MentionSuggestionItem>>,
  });
}
